import moment from 'moment';

import { IMandatory } from '~/src/dash/components/live_logs/live-logs-general-table';
import { DemandSources } from '~/src/dash/globals/demand-sources';

// compare the stringified version of the live logs to the search field (case-insensitive)
export function filterLiveLogs<TRow extends IMandatory>(
  liveLogs: TRow[],
  searchFilter: string,
  keysToSearch?: string[],
): TRow[] {
  if (!liveLogs) {
    return [];
  }
  // globally update the bidder type to reflect the type shown on the bidder list view
  liveLogs = liveLogs.map((liveLog) => {
    if (liveLog.bidderType === DemandSources.publica) {
      return Object.assign(liveLog, { bidderType: 'vast' });
    } else if (liveLog.bidderType === DemandSources.publicaCampaign) {
      return Object.assign(liveLog, { bidderType: 'directCampaign' });
    } else {
      return liveLog;
    }
  });

  const searchFilterLower = searchFilter.toLowerCase();
  return liveLogs.filter((liveLog: TRow) => {
    const mappedLiveLog = {
      ...liveLog,
      timestamp: moment(liveLog.timestamp).format('DD-MM-YYYY HH:mm:ss'),
    };

    let liveLogStr, liveLogStrLower;
    if (!keysToSearch || !keysToSearch.length) {
      liveLogStr = JSON.stringify(mappedLiveLog);
      liveLogStrLower = liveLogStr.toLowerCase();
      return liveLogStrLower.includes(searchFilterLower);
    } else {
      const searchLiveLog: { [key: string]: any } = {};
      for (const key of Object.keys(mappedLiveLog)) {
        if (keysToSearch.includes(key)) {
          searchLiveLog[key] = mappedLiveLog[key];
        }
      }
      liveLogStr = JSON.stringify(searchLiveLog);
      liveLogStrLower = liveLogStr.toLowerCase();
    }

    return liveLogStrLower.includes(searchFilterLower);
  });
}

export function filterLiveLogsByGarmScoreWrapper<TRow extends IMandatory>(
  liveLogs: TRow[],
  searchFilter: string,
  garmScoreFilter: string,
): TRow[] {
  if (!liveLogs) {
    return [];
  }

  const filteredLogs = filterLiveLogs(liveLogs, searchFilter);

  // If "All" is selected, return all results after text filtering
  if (garmScoreFilter === 'All' || garmScoreFilter === '') {
    return filteredLogs;
  }

  // Helper function to check if GARM scores contain the specified risk level
  const hasGarmRiskLevel = (garmScoresString: string, targetLevel: string): boolean => {
    if (!garmScoresString) {
      return false; // Empty scores don't match any filter
    }

    // Map filter levels to GARM score values
    const levelMap: { [key: string]: string } = {
      High: 'H',
      Medium: 'M',
      Low: 'L',
      Floor: 'F',
      Safe: 'S',
    };

    const targetValue = levelMap[targetLevel];
    if (!targetValue) {
      return false;
    }

    // If the target value is 'S', all scores must be 'S'
    if (targetValue === 'S') {
      return garmScoresString.split(',').every((score) => {
        const parts = score.split(':');
        return parts.length === 2 && parts[1] === 'S';
      });
    }

    // Check if any score has the target value
    const scores = garmScoresString.split(',');
    return scores.some((score) => {
      const parts = score.split(':');
      return parts.length === 2 && parts[1] === targetValue;
    });
  };

  // Filter by GARM score level
  return filteredLogs.filter((log: TRow) => {
    const garmScoresField: string = (log.garmScores as string) || (log.garmScore as string) || '';
    return hasGarmRiskLevel(garmScoresField, garmScoreFilter);
  });
}
